import * as React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import i18n, { bindI18nToStore } from '@/i18n/config';
import { I18nextProvider } from 'react-i18next';
import { applyDocumentChrome, useUiStore } from '@/stores/ui';
import { shouldRetryQuery } from '@/lib/queryRetry';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: shouldRetryQuery,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
});

interface ProvidersProps {
  children: React.ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  React.useEffect(() => {
    // Apply theme + dir based on the persisted store value.
    applyDocumentChrome(useUiStore.getState());
    const unsubChrome = useUiStore.subscribe((s) => applyDocumentChrome(s));
    const unsubI18n = bindI18nToStore();

    // React to OS color-scheme changes when theme === 'system'.
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onMql = () => {
      if (useUiStore.getState().theme === 'system') {
        applyDocumentChrome(useUiStore.getState());
      }
    };
    mql.addEventListener('change', onMql);

    return () => {
      unsubChrome();
      unsubI18n();
      mql.removeEventListener('change', onMql);
    };
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        {children}
        <Toaster
          richColors
          // Centred, not bottom-right: the side drawers (copy editor, order
          // detail) put their primary actions in a bottom-right footer, and a
          // stacked toast sat on top of them — blocking the very button that
          // produced it. Centre is also direction-neutral, so RTL needs no
          // second rule.
          position="bottom-center"
          // sonner respects <html dir>; these tokens carry brand colors.
          toastOptions={{
            style: {
              fontFamily: 'inherit',
            },
          }}
        />
      </QueryClientProvider>
    </I18nextProvider>
  );
}
